import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type {
  ApiRequestBody,
  ApiResponseBody,
  ApiSearchQuery,
} from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { downloadFile } from '@/lib/api/utils';
import { queryClient } from '@/lib/queryClient';

import type {
  FleetDetailApiResponse,
  FleetsCountApiResponse,
  FleetsListApiResponse,
} from './-types';

/**
 * Fleet Main Types
 */
export type FleetMainType = 'VEHICLE' | 'SHIP' | 'AIRCRAFT';
export const FleetMainType = {
  VEHICLE: 'VEHICLE',
  SHIP: 'SHIP',
  AIRCRAFT: 'AIRCRAFT',
} as const satisfies Record<FleetMainType, string>;

/**
 * Keys for mutation
 */
const FLEETS_API_KEYS = {
  all: ['fleet'] as const,
  list: (params: GetFleetsListQuery) =>
    [...FLEETS_API_KEYS.all, 'list', params] as const,
  count: (params: GetFleetsCountQuery) =>
    [...FLEETS_API_KEYS.all, 'count', params] as const,
  detail: (fleetsId: string) =>
    [...FLEETS_API_KEYS.all, 'detail', fleetsId] as const,
  avatarPresignedUrl: (fleetsId: string) =>
    [...FLEETS_API_KEYS.all, 'avatarPresignedUrl', fleetsId] as const,
  export: ['export'] as const,
} as const;

/**
 * POST /v2/fleets/{fleetId}/avatar-presigned-url
 */
export const usePostFleetsAvatarPresignedUrl = (fleetId: string) => {
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.POST(
        '/v2/fleets/{fleetId}/avatar-presigned-url',
        { params: { path: { fleetId } } },
      );
      return getDataOrThrowApiError<{ url: string }>(res).url;
    },
  });
};

/**
 * GET /v2/fleets (APIスキーマ準拠)
 */
export type GetFleetsListQuery = ApiSearchQuery<'/v2/fleets', 'get'>;

// ソート用の型定義
export type FleetsSortField =
  | 'fleetName'
  | 'fleetId'
  | 'fleetType'
  | 'subType'
  | 'fleetCategory'
  | 'manufacturer'
  | 'model'
  | 'status'
  | 'locationName'
  | 'acquisitionDate'
  | 'updatedAt'
  | 'createdAt';

export type SortDirection = 'asc' | 'desc';

export type GetFleetsListResponse = ApiResponseBody<'/v2/fleets', 'get'>;
export type FleetsListItem = GetFleetsListResponse extends { items: unknown[] }
  ? GetFleetsListResponse['items'][0]
  : import('./-types').FleetItem;

// TODO: スキーマから正しいEnumを取得できるようになったら修正
export type FleetsCategory = 'ALL' | 'DISPOSED' | 'UNDER_MAINTENANCE';
export const FleetsCategory = {
  ALL: 'ALL',
  DISPOSED: 'DISPOSED',
  UNDER_MAINTENANCE: 'UNDER_MAINTENANCE',
} as const;

// APIクエリ用のステータス型（APIが期待する値）
export type FleetsQueryStatus =
  | 'ALL'
  | 'IN_USE'
  | 'AVAILABLE'
  | 'MAINTENANCE'
  | 'RETIRED';

// アプリケーション内で使用する詳細なステータス型
export type FleetsStatus =
  | 'IN_USE'
  | 'AVAILABLE'
  | 'MAINTENANCE'
  | 'RESERVED'
  | 'STORED'
  | 'DISPOSED'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'DECOMMISSIONED';

// 予約ステータスの型定義
export type ReservationStatus =
  | 'AVAILABLE'
  | 'RESERVED'
  | 'IN_USE'
  | 'MAINTENANCE'
  | 'CLEANING'
  | 'INSPECTION'
  | 'NOT_AVAILABLE'
  | 'STORED';

export const ReservationStatus = {
  AVAILABLE: 'AVAILABLE',
  RESERVED: 'RESERVED',
  IN_USE: 'IN_USE',
  MAINTENANCE: 'MAINTENANCE',
  CLEANING: 'CLEANING',
  INSPECTION: 'INSPECTION',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  STORED: 'STORED',
} as const;

export const FleetsQueryStatus = {
  ALL: 'ALL',
  IN_USE: 'IN_USE',
  AVAILABLE: 'AVAILABLE',
  MAINTENANCE: 'MAINTENANCE',
  RETIRED: 'RETIRED',
} as const satisfies Record<FleetsQueryStatus, string>;

export const FleetsStatus = {
  IN_USE: 'IN_USE',
  AVAILABLE: 'AVAILABLE',
  MAINTENANCE: 'MAINTENANCE',
  RESERVED: 'RESERVED',
  STORED: 'STORED',
  DISPOSED: 'DISPOSED',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  DECOMMISSIONED: 'DECOMMISSIONED',
} as const;

export const useGetFleetsList = (query: GetFleetsListQuery) => {
  return useQuery({
    queryKey: FLEETS_API_KEYS.list(query),
    queryFn: async () => {
      const res = await apiClient.GET('/v2/fleets', {
        params: { query },
      });
      // 型アサーションで明示的に型を指定
      const data = getDataOrThrowApiError(
        res,
      ) as unknown as FleetsListApiResponse;

      // APIレスポンスのフィールド名を統一 (total -> count)
      return {
        items: data.items,
        count: data.total,
        page: data.page,
        pageSize: data.perPage,
      };
    },
  });
};

// Fleet-countエンドポイントはまだ存在しないため、一時的な定義
export type GetFleetsCountQuery = {
  status?: FleetsStatus;
};

export type GetFleetsCountResponse = {
  VEHICLE: number;
  SHIP: number;
  AIRCRAFT: number;
  ALL: number;
};

// mainTypeごとに件数を取得するフック
export const useGetFleetsCount = () => {
  return useQuery({
    queryKey: FLEETS_API_KEYS.count({}),
    queryFn: async (): Promise<GetFleetsCountResponse> => {
      // 各mainTypeごとにAPIを呼び出して件数を取得
      const [vehicleRes, shipRes, aircraftRes, allRes] = await Promise.all([
        apiClient.GET('/v2/fleets', {
          params: {
            query: {
              mainType: FleetMainType.VEHICLE,
              pageSize: 1,
              page: 1,
            } as Record<string, unknown>,
          },
        }),
        apiClient.GET('/v2/fleets', {
          params: {
            query: {
              mainType: FleetMainType.SHIP,
              pageSize: 1,
              page: 1,
            } as Record<string, unknown>,
          },
        }),
        apiClient.GET('/v2/fleets', {
          params: {
            query: {
              mainType: FleetMainType.AIRCRAFT,
              pageSize: 1,
              page: 1,
            } as Record<string, unknown>,
          },
        }),
        apiClient.GET('/v2/fleets', {
          params: {
            query: {
              pageSize: 1,
              page: 1,
            } as Record<string, unknown>,
          },
        }),
      ]);

      const vehicleData = getDataOrThrowApiError(
        vehicleRes,
      ) as unknown as FleetsCountApiResponse;
      const shipData = getDataOrThrowApiError(
        shipRes,
      ) as unknown as FleetsCountApiResponse;
      const aircraftData = getDataOrThrowApiError(
        aircraftRes,
      ) as unknown as FleetsCountApiResponse;
      const allData = getDataOrThrowApiError(
        allRes,
      ) as unknown as FleetsCountApiResponse;

      return {
        VEHICLE: vehicleData.total ?? 0,
        SHIP: shipData.total ?? 0,
        AIRCRAFT: aircraftData.total ?? 0,
        ALL: allData.total ?? 0,
      };
    },
  });
};

/**
 * GET /v2/fleets/{id} (APIスキーマ準拠)
 */

// APIスキーマが正しく生成されていない場合の型定義
export interface FleetDetailResponse {
  id: string; // v2 APIではidが主キー
  fleetId?: string;
  fleetName: string;
  fleetType: string; // "VEHICLE" | "SHIP" | "AIRCRAFT"
  fleetTypeCode?: string;
  fleetTypeName?: string;
  mainType?: 'VEHICLE' | 'SHIP' | 'AIRCRAFT';
  fleetCategoryCode?: string;
  fleetCategoryName?: string;
  fleetCategoryId?: string;
  organizationId: string;
  status?:
    | 'IN_USE'
    | 'AVAILABLE'
    | 'MAINTENANCE'
    | 'RESERVED'
    | 'STORED'
    | 'DISPOSED'
    | 'ACTIVE'
    | 'INACTIVE'
    | 'DECOMMISSIONED';
  registrationNumber?: string;
  serialNumber?: string;
  model?: string;
  manufacturer?: string;
  yearOfManufacture?: number;
  owner?: string;
  ownerName?: string;
  ownershipType?: string;
  ownerPurpose?: string;
  usagePurpose?: string;
  leaseCompany?: string;
  leaseContractId?: string;
  gpsTrackingData?: boolean;
  gpsCurrentLat?: number;
  gpsCurrentLng?: number;
  remarks?: string;
  purchaseDate?: string;
  purchasePrice?: string;
  disposalDate?: string;
  disposalPrice?: string;
  disposalReason?: string;
  currentValue?: string;
  depreciationRate?: string;
  lastValuationDate?: string;
  notes?: string;
  tags?: string[];
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  placeId?: string;
  place?: {
    placeId: string;
    placeName: string;
  };
  responsiblePersonId?: string;
  responsiblePerson?: {
    userId: string;
    userName: string;
  };
  vehicle?: {
    vehicleId: string;
    fleetId: string;
    vehicleType?: string;
    engineType?: string;
    fuelType?: string;
    engineCapacity?: string;
    powerOutput?: string;
    torque?: string;
    transmission?: string;
    drivetrain?: string;
    seatingCapacity?: number;
    doorsNumber?: number;
    color?: string;
    bodyType?: string;
    length?: string;
    width?: string;
    height?: string;
    wheelbase?: string;
    groundClearance?: string;
    curbWeight?: string;
    grossVehicleWeight?: string;
    cargoCapacity?: string;
    towingCapacity?: string;
    fuelTankCapacity?: string;
    fuelEfficiency?: string;
    co2Emissions?: string;
    emissionStandard?: string;
    plateNumber?: string;
    registrationExpiry?: string;
    insurancePolicyNumber?: string;
    insuranceExpiry?: string;
    roadTaxExpiry?: string;
    inspectionExpiry?: string;
    warrantyExpiry?: string;
    maintenanceSchedule?: string;
    lastServiceDate?: string;
    lastServiceOdometer?: string;
    nextServiceDate?: string;
    nextServiceOdometer?: string;
    currentOdometer?: string;
    totalDistance?: string;
    averageDailyDistance?: string;
    gpsDeviceId?: string;
    vehicleIdentificationNumber?: string;
    parkingLocation?: string;
    homeBaseLocationId?: string;
    safetyRating?: string;
    features?: string[];
    customFields?: Record<string, unknown>;
  };
  ship?: {
    shipId: string;
    fleetId: string;
    callSign?: string;
    mmsi?: string;
    vesselType?: string;
    grossTonnage?: string;
    netTonnage?: string;
    deadweightTonnage?: string;
    length?: string;
    beam?: string;
    depth?: string;
    draft?: string;
    yearBuilt?: string;
    shipbuilder?: string;
    flagState?: string;
    homePort?: string;
    classificationSociety?: string;
    classCertificate?: string;
    classCertificateExpiry?: string;
    engineType?: string;
    enginePower?: string;
    cruisingSpeed?: string;
    maximumSpeed?: string;
    fuelCapacity?: string;
    fuelConsumption?: string;
    waterCapacity?: string;
    ballastCapacity?: string;
    cargoCapacity?: string;
    passengerCapacity?: number;
    crewCapacity?: number;
    hullMaterial?: string;
    propulsionType?: string;
    propellerType?: string;
    navigationArea?: string;
    shipCertificate?: string;
    shipCertificateExpiry?: string;
    safetyCertificate?: string;
    safetyCertificateExpiry?: string;
    pollutionCertificate?: string;
    pollutionCertificateExpiry?: string;
    loadLineCertificate?: string;
    loadLineCertificateExpiry?: string;
    radioCertificate?: string;
    radioCertificateExpiry?: string;
    cargoGearCertificate?: string;
    cargoGearCertificateExpiry?: string;
    hullInsurancePolicy?: string;
    hullInsuranceExpiry?: string;
    pAndIInsurancePolicy?: string;
    pAndIInsuranceExpiry?: string;
    lastDryDockDate?: string;
    nextDryDockDate?: string;
    lastSurveyDate?: string;
    nextSurveyDate?: string;
    lastInspectionDate?: string;
    nextInspectionDate?: string;
    layUpStatus?: boolean;
    layUpStartDate?: string;
    layUpEndDate?: string;
    layUpLocation?: string;
    charterStatus?: string;
    chartererName?: string;
    charterStartDate?: string;
    charterEndDate?: string;
    charterRate?: string;
    operatingCosts?: string;
    averageFuelCost?: string;
    annualMaintenanceCost?: string;
    specialSurveyReserve?: string;
    marketValue?: string;
    scrapValue?: string;
    replacementCost?: string;
    bookValue?: string;
    customFields?: Record<string, unknown>;
    accidentLog?: string;
    portStateControlRecords?: string;
    detentionHistory?: string;
    specialNavigationPermit?: string;
  };
  aircraft?: {
    aircraftId: string;
    fleetId: string;
    tailNumber?: string;
    aircraftType?: string;
    variant?: string;
    category?: string;
    classType?: string;
    maximumTakeoffWeight?: string;
    maximumLandingWeight?: string;
    emptyWeight?: string;
    payloadCapacity?: string;
    fuelCapacity?: string;
    wingspan?: string;
    wingArea?: string;
    fuselageLength?: string;
    height?: string;
    cabinWidth?: string;
    cabinHeight?: string;
    cabinLength?: string;
    cargoVolume?: string;
    passengerCapacity?: number;
    crewCapacity?: number;
    seatConfiguration?: string;
    maximumRange?: string;
    cruiseSpeed?: string;
    maximumSpeed?: string;
    serviceSeiling?: string;
    takeoffDistance?: string;
    landingDistance?: string;
    fuelBurn?: string;
    engineCount?: number;
    engineType?: string;
    engineModel?: string;
    engineThrust?: string;
    engineHours?: string;
    propellerType?: string;
    avionicsPackage?: string;
    autopilotSystem?: string;
    flightManagementSystem?: string;
    registrationCountry?: string;
    registrationExpiry?: string;
    airworthinessCertificate?: string;
    airworthinessExpiry?: string;
    insurancePolicy?: string;
    insuranceExpiry?: string;
    lastAnnualInspection?: string;
    nextAnnualInspection?: string;
    last100HourInspection?: string;
    next100HourInspection?: string;
    lastEngineOverhaul?: string;
    nextEngineOverhaul?: string;
    lastPropellerOverhaul?: string;
    nextPropellerOverhaul?: string;
    totalAirframeHours?: string;
    totalCycles?: string;
    hourssinceLastMaintenance?: string;
    cyclesSinceLastMaintenance?: string;
    maximumCertificatedAltitude?: string;
    noiseCategory?: string;
    transponderCode?: string;
    emergencyEquipment?: string[];
    modifications?: string[];
    homeBaseAirportId?: string;
    operatingBase?: string;
    flightPermitNumber?: string;
    flightPermitExpiry?: string;
    customsRegistration?: string;
    exportCertificate?: string;
    leasingStatus?: string;
    lessorName?: string;
    leaseStartDate?: string;
    leaseEndDate?: string;
    monthlyLeaseRate?: string;
    hourlyOperatingCost?: string;
    maintenanceReserveRate?: string;
    marketValue?: string;
    bluebookValue?: string;
    replacementCost?: string;
    residualValue?: string;
    depreciationBasis?: string;
    maintenanceProgram?: string;
    serviceHistory?: string;
    accidentHistory?: string;
    equipmentLog?: string;
    customFields?: Record<string, unknown>;
  };
}

export const useGetFleets = (fleetId: string) => {
  return useSuspenseQuery({
    queryKey: FLEETS_API_KEYS.detail(fleetId),
    queryFn: async () => {
      const res = await apiClient.GET('/v2/fleets/{id}', {
        params: { path: { id: fleetId } },
      });
      // 型アサーションで明示的に型を指定
      const data = getDataOrThrowApiError(
        res,
      ) as unknown as FleetDetailApiResponse;

      // mainTypeをfleetTypeとして設定
      const dataWithMainType = data as FleetDetailApiResponse & {
        mainType?: string;
      };
      if (
        dataWithMainType &&
        !dataWithMainType.fleetType &&
        dataWithMainType.mainType
      ) {
        dataWithMainType.fleetType = dataWithMainType.mainType;
      }

      return data;
    },
  });
};

/**
 * POST /v2/fleets
 */
export type PostFleetsRequest = ApiRequestBody<'/v2/fleets', 'post'>;
export type PostFleetsResponse = ApiResponseBody<'/v2/fleets', 'post'>;

// APIスキーマが正しく生成されていない場合の型定義
export interface CreateFleetRequestDto {
  // 必須フィールド
  fleetType: FleetMainType; // "VEHICLE" | "SHIP" | "AIRCRAFT"
  fleetName: string;

  // オプションフィールド
  fleetId?: string; // 業務上の一意識別子（ユーザーが入力可能）
  status?: FleetsStatus;
  ownershipType?: string;
  ownerPurpose?: string;
  usagePurpose?: string;
  acquisitionDate?: string;
  acquisitionCost?: number;
  disposalDate?: string;
  disposalReason?: string;
  departmentId?: string;
  placeId?: string;
  responsiblePersonId?: string;

  // ShipStep11Formで使用されているフィールド
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  yearOfManufacture?: number;
  ownerName?: string;
  leaseCompany?: string;
  leaseContractId?: string;

  // タイプ別の詳細データ
  ship?: CreateFleetShipDto;
  vehicle?: CreateFleetVehicleDto;
  aircraft?: CreateFleetAircraftDto;

  // 関連テーブルデータ (Vehicle用)
  vehicleOperation?: Record<string, unknown>;
  vehicleMaintenance?: Record<string, unknown>;
  vehicleUsage?: Record<string, unknown>;
  vehicleFuel?: Record<string, unknown>;
  vehicleInsurance?: Record<string, unknown>;
  vehicleAccident?: Record<string, unknown>;
  vehicleDriver?: Record<string, unknown>;

  // Fleet共通整備データ
  maintenance?: Record<string, unknown>;
}

export interface CreateFleetShipDto {
  shipId?: string;
  shipName?: string;
  imoNumber?: string;
  mmsiNumber?: string;
  callSign?: string;
  flagState?: string;
  classificationSociety?: string;
  shipType?: string;
  hullNumber?: string;
  builder?: string;
  buildDate?: string;
  grossTonnage?: number;
  deadweightTonnage?: number;
  navigationArea?: string;
  lengthOverall?: number;
  breadth?: number;
  draft?: number;
  engineSpecs?: string;
  propulsionType?: string;
  fuelType?: string;
  fuelCapacity?: number;
  ballastCapacity?: number;
  cargoGear?: string;
  crewCapacity?: number;
  passengerCapacity?: number;
  managementCompany?: string;
  operationalManagerId?: string;
  department?: string;
  homePort?: string;
  insuranceInfo?: Record<string, unknown> | string;
  accidentLog?: Record<string, unknown> | string;
  vesselLicense?: string;
  radioLicense?: string;
  specialNavigationPermit?: string;
  emissionCertificates?: string;
  lifeboatCount?: number;
  lifejacketCount?: number;
}

export interface CreateFleetVehicleDto {
  vehicleId?: string;
  licensePlateNumber?: string;
  vin?: string;
  manufacturer?: string;
  vehicleModel?: string;
  vehicleType?: string;
  vehicleCategory?: string;
  vehicleColor?: string;
  registrationDate?: string;
  status?: string;
  vehicleDimensions?: string;
  curbWeight?: number;
  maximumPayload?: number;
  engineType?: string;
  engineDisplacement?: number;
  fuelType?: string;
  fuelTankCapacity?: number;
  fuelEfficiency?: number;
  driveType?: string;
  transmission?: string;
  // Fleet共通の整備・法定管理フィールド
  maintenanceId?: string;
  maintenanceDate?: string;
  maintenanceType?: string;
  maintenanceDescription?: string;
  maintenanceCost?: number;
  maintenanceProvider?: string;
  inspectionExpiryDate?: string;
  nextMaintenanceDate?: string;
  seatCapacity?: number;
  responsiblePerson?: string;
  assignedDepartment?: string;
  assignedUser?: string;
  assignedDriverId?: string;
  usagePurpose?: string;
  placeId?: string;
  odometerReading?: number;
  installedEquipment?: string;
  vehicleSensors?: string;
  driverLicenseInfo?: string;
  specialPermit?: string;
}

export interface CreateFleetAircraftDto {
  aircraftId?: string;
  registrationNumber?: string;
  icaoAircraftCode?: string;
  iataAircraftCode?: string;
  manufacturer?: string;
  model?: string;
  manufacturerSerialNo?: string;
  aircraftType?: string;
  manufactureDate?: string;
  countryOfRegistration?: string;
  totalFlightHours?: number;
  totalCycles?: number;
  seatCapacity?: number;
  crewCapacity?: number;
  cargoCapacity?: number;
  engineType?: string;
  engineModel?: string;
  numberOfEngines?: number;
  engineThrust?: number;
  fuelType?: string;
  fuelCapacity?: number;
  emptyWeight?: number;
  maximumTakeoffWeight?: number;
  maximumLandingWeight?: number;
  wingspan?: number;
  lengthOverall?: number;
  height?: number;
  cruiseSpeed?: number;
  range?: number;
  airworthinessCertificate?: string;
  registrationCertificate?: string;
  aocCertificate?: string;
  aircraftLicenseNumber?: string;
  radioLicense?: string;
  uavOperationPermit?: string;
  insuranceInformation?: string;
}

export const usePostFleets = () => {
  return useMutation({
    mutationFn: async (
      data: PostFleetsRequest,
    ): Promise<PostFleetsResponse> => {
      const res = await apiClient.POST('/v2/fleets', {
        body: data,
      });
      return getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FLEETS_API_KEYS.all });
    },
  });
};

/**
 * PATCH /v2/fleets/{id}
 */
export type PatchFleetsRequest = ApiRequestBody<'/v2/fleets/{id}', 'patch'>;
export type PatchFleetsResponse = ApiResponseBody<'/v2/fleets/{id}', 'patch'>;

export const usePatchFleets = (fleetId: string) => {
  return useMutation({
    mutationFn: async (
      data: PatchFleetsRequest,
    ): Promise<PatchFleetsResponse> => {
      const res = await apiClient.PATCH('/v2/fleets/{id}', {
        params: { path: { id: fleetId } },
        body: data,
      });
      return getDataOrThrowApiError(res) as PatchFleetsResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FLEETS_API_KEYS.all });
    },
  });
};

/**
 * GET /v2/fleets/csv-export
 */
export type GetFleetsCsvQuery = {
  mode?: string;
  encoding?: string;
  fleetType?: string;
};
export type GetFleetsCsvResponse = {
  mode?: string;
  encoding?: string;
  url?: string;
};

export const useGetFleetsCsv = (query: GetFleetsCsvQuery = {}) => {
  return useMutation({
    mutationKey: FLEETS_API_KEYS.export,
    mutationFn: async () => {
      const res = await apiClient.GET('/v2/fleets/csv-export', {
        params: {
          query: {
            mode: query.mode || 'view',
            encoding: query.encoding || 'UTF-8',
            fleetType: query.fleetType || 'ALL',
          },
        },
      });

      // レスポンスデータを取得
      const data = res.data;
      if (res.error || !data) {
        throw new Error('CSV export failed');
      }

      return data as unknown as GetFleetsCsvResponse;
    },
    onSuccess: async (data) => {
      const csvData = data as GetFleetsCsvResponse;
      // URLベースのダウンロード
      if (csvData?.url) {
        const filename = `fleets-export-${new Date().toISOString().split('T')[0]}.csv`;
        await downloadFile(csvData.url, filename);
      }
    },
  });
};
